import React, { useContext, useState } from "react"

import { RouteComponentProps } from "react-router"
import { gql, useMutation, useQuery } from "urql"

import { AlertLevel, Color } from "~tailwindui/types/enums"

import NotFound from "~Tutor/Pages/NotFound"
import TutorContext from "~Tutor/TutorContext"
import { path } from "~Tutor/TutorRoutes"
import {
  AlertMessageBox,
  Button,
  H3,
  StyledLink,
  WithLoadingIndicator,
} from "~tailwindui/Basics"
import DescriptionItem from "~tailwindui/Basics/DescriptionItem"
import H4 from "~tailwindui/Basics/H4"
import Panel from "~tailwindui/Basics/Panel"

import titlecase from "src/titlecase"
import { RunMutation } from "src/types"
import { MatchParams } from "src/types/MatchParams"

import LocalTime from "components/LocalTime"

import ReplacementRequest from "../components/ReplacementRequest"

import FutureSessions from "./FutureSessions"
import PastSessions from "./PastSessions"

const Details: React.FC<RouteComponentProps<MatchParams>> = ({ match }) => {
  const studyGroupId = match.params.id
  const { viewer } = useContext(TutorContext)
  const tutorId = viewer.id

  const [error, setError] = useState<string | null>()
  const [message, setMessage] = useState<string | null>()
  const [result] = useQuery({
    query,
    variables: { studyGroupId, tutorId },
  })

  const [_, runMutation]: [any, RunMutation] = useMutation(
    enqueueIntroductoryTextMutation
  )

  const enqueueIntroductoryText = () => {
    runMutation({ studyGroupId }).then(result => {
      const { failures, success } = result.data.enqueueIntroductoryText
      if (failures.length > 0) setError(failures[0].message)
      else setMessage(success.message)
    })
  }

  if (result.data && !result.data.studyGroup) return <NotFound />

  return (
    <WithLoadingIndicator result={result}>
      {({ data: { studyGroup } }) => (
        <>
          {studyGroup.minTutorConsistencyRate &&
            studyGroup.hasAvailabilities && (
              <AlertMessageBox level={AlertLevel.Warning}>
                <div>
                  This study group requires a tutor consistency rate of{" "}
                  {studyGroup.minTutorConsistencyRate}%.
                  {studyGroup.subRequestsLeft ? (
                    studyGroup.subRequestsLeft > 5 ? (
                      <div>
                        You will be notified if you approach the limit on
                        substitute requests.
                      </div>
                    ) : studyGroup.subRequestsLeft > 0 ? (
                      <div>
                        You have {studyGroup.subRequestsLeft} substitute
                        request(s) remaining.
                      </div>
                    ) : (
                      <div>
                        You are not allowed to request any more substitutes. If
                        you know that you are going to miss another session,
                        please give up the group as soon as possible.
                      </div>
                    )
                  ) : null}
                </div>
              </AlertMessageBox>
            )}

          <H3 className="flex items-center">Group Information</H3>
          <hr className="mb-4" />

          {studyGroup.studyGroupGeneration &&
            !studyGroup.introductoryTextEnqueued && (
              <Panel>
                <p className="mb-4">
                  Prior to the first session, click the button below to have an
                  automated text sent to the students and parents in your group,
                  letting them know your name and the days/times that the group
                  will meet.
                </p>

                <Button onClick={enqueueIntroductoryText} color={Color.Teal}>
                  Send Introductory Text
                </Button>
                <br />
                {error && <AlertMessageBox>{error}</AlertMessageBox>}
                {message && (
                  <AlertMessageBox level={AlertLevel.Info}>
                    {message}
                  </AlertMessageBox>
                )}
              </Panel>
            )}

          <div className="flex flex-col gap-4 md:flex-row">
            <Panel className="grow md:w-1/2">
              <DescriptionItem label="Name" value={studyGroup.name} />
              {studyGroup.details && (
                <DescriptionItem label="Details" value={studyGroup.details} />
              )}
              <DescriptionItem
                label="Subjects"
                value={studyGroup.subjects
                  .map(subject => subject.name)
                  .join(", ")}
              />

              {studyGroup.availabilities.length > 0 && (
                <DescriptionItem
                  label="Session Times"
                  value={
                    <ul>
                      {studyGroup.availabilities.map(availability => (
                        <li key={availability.id}>
                          {titlecase(availability.weekday)}{" "}
                          <LocalTime
                            omitDate
                            timestamp={availability.startsAt}
                          />{" "}
                          -{" "}
                          <LocalTime omitDate timestamp={availability.endsAt} />
                        </li>
                      ))}
                    </ul>
                  }
                />
              )}

              {studyGroup.courseSection && (
                <>
                  <DescriptionItem
                    label="Dates"
                    value={
                      <span>
                        <LocalTime
                          timestamp={studyGroup.courseSection.startsOn}
                        />{" "}
                        -{" "}
                        <LocalTime
                          timestamp={studyGroup.courseSection.endsOn}
                        />
                      </span>
                    }
                  />
                  <DescriptionItem
                    label="Meets"
                    value={
                      <ul>
                        {studyGroup.courseSection.availabilities.map(
                          availability => (
                            <li key={availability.id}>
                              {titlecase(availability.weekday)}{" "}
                              <LocalTime
                                omitDate
                                timestamp={availability.startsAt}
                              />{" "}
                              -{" "}
                              <LocalTime
                                omitDate
                                timestamp={availability.endsAt}
                              />
                            </li>
                          )
                        )}
                      </ul>
                    }
                  />
                </>
              )}
              {studyGroup.organization && (
                <>
                  <DescriptionItem
                    label="Organization"
                    value={studyGroup.organization.name}
                  />
                  {studyGroup.organization.details?.length > 0 && (
                    <DescriptionItem
                      label="Organization Details"
                      value={studyGroup.organization.details}
                    />
                  )}
                  <DescriptionItem
                    label="Organization Time Zone"
                    value={studyGroup.organization.emailTimeZone}
                  />
                </>
              )}
            </Panel>

            <Panel className="md:max-w-md md:grow">
              <H4 className="mb-2">Students</H4>
              <ul className="space-y-2">
                {studyGroup.students.map(student => (
                  <li key={student.id}>
                    <StyledLink
                      to={path("studentDetails", {
                        id: student.id,
                      })}
                    >
                      {student.fullName}
                    </StyledLink>
                    {student.notes && (
                      <span className="italic"> - {student.notes}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel className="min-w-max">
              <H4 className="text-center">Actions</H4>
              <div className="flex flex-col justify-center space-y-2">
                {studyGroup.canRequestReplacement && (
                  <ReplacementRequest id={studyGroup.id} />
                )}
                {studyGroup.tutorMaySchedule && (
                  <StyledLink
                    to={path("studyGroupScheduleSession", {
                      id: studyGroup.id,
                    })}
                    button
                    color={Color.Teal}
                    className="my-2"
                  >
                    Schedule Session
                  </StyledLink>
                )}
              </div>
            </Panel>
          </div>

          {studyGroup.lastStudyGroupTutorReplacement && (
            <Panel>
              <ul>
                <li key={studyGroup.lastStudyGroupTutorReplacement.id}>
                  <div className="mb-2 font-medium">
                    Notes from{" "}
                    {
                      studyGroup.lastStudyGroupTutorReplacement.originalTutor
                        .fullName
                    }{" "}
                    (previous tutor):
                  </div>
                  <p className="italic text-red-700">
                    {studyGroup.lastStudyGroupTutorReplacement.details}
                  </p>
                </li>
              </ul>
            </Panel>
          )}

          <H3 className="flex items-center">Group Sessions</H3>
          <hr className="mb-4" />

          <FutureSessions studyGroupId={studyGroupId} />
          <PastSessions studyGroupId={studyGroupId} />
        </>
      )}
    </WithLoadingIndicator>
  )
}

const query = gql`
  query StudyGroupDetails($studyGroupId: ID!, $tutorId: ID!) {
    studyGroup(id: $studyGroupId) {
      id
      name
      details
      minTutorConsistencyRate
      hasAvailabilities
      tutorMaySchedule
      maxSubsEstimate
      introductoryTextEnqueued
      subRequestsLeft(tutorId: $tutorId)
      oneOnOne
      canRequestReplacement
      subjects {
        id
        name
      }
      organization {
        id
        name
        details
        emailTimeZone
      }
      lastStudyGroupTutorReplacement {
        id
        details
        originalTutor {
          id
          fullName
        }
      }
      studyGroupGeneration {
        id
      }
      availabilities {
        id
        weekday
        startsAt
        endsAt
      }
      courseSection {
        id
        startsOn
        endsOn
        availabilities {
          id
          weekday
          startsAt
          endsAt
        }
      }
      students {
        id
        fullName
        school {
          id
          name
        }
      }
    }
  }
`

const enqueueIntroductoryTextMutation = gql`
  mutation tutorEnqueueIntroductoryText($studyGroupId: ID!) {
    enqueueIntroductoryText(studyGroupId: $studyGroupId) {
      failures {
        message
      }
      success {
        message
      }
    }
  }
`

export default Details
